import React, { useEffect, useRef, useState } from 'react';
import { Button, Calendar, InputNumber, Popover } from 'antd';
import dayjs from 'dayjs';
import { CalendarOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { WorkflowInvoiceDraft } from '../../../types/workflowInvoice';
import { fetchInvoiceDatedRate } from '../api/invoiceExchangeRate';
import { POLISH_DECIMAL_INPUT_NUMBER_PROPS } from '../../../utils/polishNumberInput';

export default function InvoiceExchangeRateField({ value, patch, disabled }: {
  value: WorkflowInvoiceDraft; patch: (next: Partial<WorkflowInvoiceDraft>) => void; disabled: boolean;
}) {
  const { t } = useTranslation('invoices');
  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const current = useRef({ value, patch, disabled }); current.current = { value, patch, disabled };
  const generation = useRef(0);
  const date = value.exchangeRateDate?.slice(0, 10) ?? (value.issueDate ? dayjs(value.issueDate).subtract(1, 'day').format('YYYY-MM-DD') : null);
  const attempted = useRef<string | undefined>(undefined);
  const load = async (requestedDate = date) => {
    if (!value.currency || !requestedDate) return;
    const request = ++generation.current;
    const original = value;
    setLoading(true); setFailed(false);
    try {
      const snapshot = await fetchInvoiceDatedRate(value.currency, requestedDate);
      const latest = current.current;
      if (generation.current !== request || latest.disabled || latest.value.id !== original.id || latest.value.status !== original.status || latest.value.currency !== original.currency
        || latest.value.exchangeRateToPLN !== original.exchangeRateToPLN
        || latest.value.exchangeRateDate !== original.exchangeRateDate || latest.value.issueDate !== original.issueDate) return;
      latest.patch({ exchangeRateToPLN: snapshot.rate, exchangeRateDate: requestedDate, exchangeRateSnapshot: snapshot });
      setOpen(false);
    } catch { if (request === generation.current) setFailed(true); }
    finally { if (request === generation.current) setLoading(false); }
  };
  useEffect(() => {
    const key = `${value.id}:${value.currency}:${date}`;
    if (!disabled && value.direction === 'PURCHASE' && value.currency && value.currency !== 'PLN' && date && value.exchangeRateToPLN == null && attempted.current !== key) {
      attempted.current = key; void load();
    }
    // User edits and late responses are checked against current above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value.id, value.currency, date, value.exchangeRateToPLN, disabled]);
  useEffect(() => () => { generation.current += 1; }, []);
  if (value.currency === 'PLN') return <div className="order-wizard__value-box">1 PLN = 1 PLN</div>;
  return <div className="invoice-fx">
    <div className="invoice-fx-input">
      <InputNumber aria-label={t('invoices:workflow.fields.exchangeRate')} value={value.exchangeRateToPLN ?? null} disabled={disabled} controls={false}
        onChange={rate => { generation.current += 1; setLoading(false); patch({ exchangeRateToPLN: rate, exchangeRateDate: null, exchangeRateSnapshot: null }); }} {...POLISH_DECIMAL_INPUT_NUMBER_PROPS} />
      {value.direction === 'PURCHASE' && <Popover trigger="click" placement="bottomRight" open={open && !disabled}
        onOpenChange={next => { if (next) setSelectedDate(date ?? dayjs().format('YYYY-MM-DD')); setOpen(next); }}
        content={<div className="invoice-fx-calendar">
          <Calendar fullscreen={false} value={dayjs(selectedDate ?? date ?? undefined)}
            disabledDate={day => day.isAfter(dayjs(), 'day') || day.isBefore('2002-01-02', 'day')}
            onSelect={day => setSelectedDate(day.format('YYYY-MM-DD'))} />
          <Button type="primary" block loading={loading} disabled={disabled || !selectedDate || !value.currency || dayjs(selectedDate).isAfter(dayjs(), 'day') || dayjs(selectedDate).isBefore('2002-01-02', 'day')}
            onClick={() => void load(selectedDate)}>{t('invoices:workflow.review.loadRate')}</Button>
        </div>}>
        <Button type="text" className="invoice-fx-calendar-button" icon={<CalendarOutlined />} disabled={disabled}
          aria-label={t('invoices:workflow.review.rateDate')} />
      </Popover>}
    </div>
    <span className="invoice-field-hint">{value.exchangeRateSnapshot
      ? t('invoices:workflow.review.rateSource', { date: dayjs(value.exchangeRateSnapshot.effectiveDate).format('DD.MM.YYYY'), table: value.exchangeRateSnapshot.tableNumber })
      : t('invoices:workflow.review.rateUnknown')}</span>
    {failed && <span role="alert" className="invoice-field-error">{t('invoices:workflow.review.rateFailed')}</span>}
  </div>;
}
